/**
 * Zuordnen eines Suchtreffers zur Akte (Dialog „Beteiligte zuordnen“).
 * Kundenfeedback 18.10.2026 (XIII): Jede Rolle ist wählbar – der Treffer
 * wird nur in der Akte mit der Rolle verknüpft (und kann in dieser Rolle
 * eingeladen werden). Endpunkte wie bisher:
 *   - Kunde:                 POST/PUT …/clients
 *   - registrierte Abteilung: PUT …/departments (Rolle passt zum Unternehmen)
 *   - sonst:                 POST …/externals (Rolle, Fahrzeug, Kontakt-Bezug)
 */
import type { ContactSearchHit } from "@/utils/contactSearchHits";
import { putRequest } from "@/services/backendApi";
import { postRequestGeneric, putRequestGeneric } from "@/services/backendApiGeneric";
import { setParticipantNotificationMute } from "@/services/caseNotificationsApi";
import { type InvitableParticipant, invitableFromCreated } from "../../components/addParticipantOverview";
import { portalEntryEmail, portalEntryTitle } from "../../portalOrganizations";
import { type CreatedParticipant, type SelectionKind, participantMutesFromSelection } from "../participantMutesFromSelection";
import { MANDANT_ROLE_KEY } from "./participantDialogModel";

/** Rollen, die fallweit gelten und nicht je Fahrzeug getrennt werden. */
const SHARED_ROLES: ReadonlySet<string> = new Set(["Rechtsanwalt"]);
const HTTP_CONFLICT = 409;

export interface AssignRequest {
  caseId: number;
  hit: ContactSearchHit;
  role: string;
  /** Ziel-Fahrzeug (null = alle Fahrzeuge des Falls) */
  vehicleId: number | null;
  /** false = Beteiligter erhält keine Fall-Benachrichtigungen */
  notify: boolean;
}

export interface AssignResult {
  /** created = neu beteiligt, exists = war bereits beteiligt */
  status: "created" | "exists";
  /** Direkt einladbarer Externer (mit E-Mail, keine Versicherung) */
  invitable: InvitableParticipant | null;
  /** Fehlermeldung, falls die Stummschaltung nicht gespeichert werden konnte */
  muteError: string;
  /** Kundenfeedback 02.11.2026 (I): keine Einladung, der Beteiligte möchte keine */
  invitationBlocked?: boolean;
}

function isConflict (error: unknown): boolean {
  return (error as { errorCode?: number } | null)?.errorCode === HTTP_CONFLICT;
}

function selectionKind (hit: ContactSearchHit, viaDepartment: boolean): SelectionKind {
  if (viaDepartment) { return "department"; }
  if (hit.source === "registered") { return "central"; }
  if (hit.source === "insurance") { return "insurance"; }
  return "contact";
}

/** Registrierte Abteilung direkt beteiligen, wenn die Rolle zum Unternehmen passt. */
function joinsViaDepartment (hit: ContactSearchHit, role: string): boolean {
  const entry = hit.portal;
  if (hit.source !== "registered" || !entry || entry.kind !== "department" || entry.departmentId == null) { return false; }
  return entry.organizationRoles.length === 0 || entry.organizationRoles.includes(role);
}

async function addDepartment (req: AssignRequest): Promise<number> {
  const entry = req.hit.portal!;
  await putRequest(
    `/protected/cases/${req.caseId}/departments`,
    { id: entry.departmentId, title: portalEntryTitle(entry), caseReference: "", idVehicle: req.vehicleId },
    undefined,
    true,
  );
  return Number(entry.departmentId);
}

async function addExternal (req: AssignRequest): Promise<{ id: number, invitationBlocked: boolean }> {
  const { hit, role } = req;
  const email = hit.portal ? portalEntryEmail(hit.portal) : hit.email;
  const created = await postRequestGeneric(`/protected/cases/${req.caseId}/externals`, {
    email: email || "",
    description: hit.name,
    companyName: hit.portal ? hit.portal.companyName : undefined,
    roles: [role],
    idVehicle: SHARED_ROLES.has(role) ? null : req.vehicleId,
    // Akten-Beteiligung: Rückverweis auf den Adressbuch-Kontakt.
    idContact: hit.source === "own" && hit.contact?.id ? hit.contact.id : undefined,
  }, true);
  return { id: Number(created?.id), invitationBlocked: !!created?.invitationBlocked };
}

/** Kunde (Mandant) anlegen: leeren Datensatz erzeugen, dann Stammdaten eintragen. */
async function addMandant (req: AssignRequest): Promise<void> {
  const c = req.hit.contact;
  const created = await postRequestGeneric(`/protected/cases/${req.caseId}/clients`, {}, true);
  const data = created?.data ?? created;
  if (!data?.id) { throw new Error("Konnte neuen Kunden nicht anlegen."); }
  const clean = (v: string | null | undefined) => (v ?? "").trim() || null;
  await putRequestGeneric(`/protected/cases/${req.caseId}/clients/${data.id}`, {
    ...data,
    salutation: clean(c?.salutation),
    firstName: clean(c?.firstName),
    lastName: clean(c?.lastName),
    company: clean(c ? c.companyName : req.hit.name),
    email: clean(req.hit.email),
    phone: clean(c?.phone),
    mobile: clean(c?.mobile),
  }, true);
}

async function applyMute (req: AssignRequest, created: CreatedParticipant): Promise<string> {
  const mutes = participantMutesFromSelection([created]);
  if (!mutes.length) { return ""; }
  try {
    await Promise.all(mutes.map((m) => setParticipantNotificationMute(req.caseId, m.participantType, m.idParticipant, { muted: true, email: m.email, name: m.name })));
    return "";
  } catch (error) {
    return (error as Error)?.message || "Stummschaltung fehlgeschlagen";
  }
}

/**
 * Ordnet den Treffer in der gewählten Rolle zu. Wirft bei Fehlern (außer
 * „bereits beteiligt“) eine Error-Instanz mit lesbarer Meldung.
 */
export async function assignHit (req: AssignRequest): Promise<AssignResult> {
  if (req.role === MANDANT_ROLE_KEY) {
    await addMandant(req);
    return { status: "created", invitable: null, muteError: "" };
  }
  const viaDepartment = joinsViaDepartment(req.hit, req.role);
  let idParticipant: number;
  let invitationBlocked = false;
  try {
    if (viaDepartment) {
      idParticipant = await addDepartment(req);
    } else {
      const ext = await addExternal(req);
      idParticipant = ext.id;
      invitationBlocked = ext.invitationBlocked;
    }
  } catch (error) {
    if (isConflict(error)) { return { status: "exists", invitable: null, muteError: "" }; }
    throw error;
  }
  const email = req.hit.portal ? portalEntryEmail(req.hit.portal) : req.hit.email;
  const created: CreatedParticipant = {
    selection: { type: selectionKind(req.hit, viaDepartment), notify: req.notify, role: req.role, name: req.hit.name, email },
    idParticipant,
  };
  const muteError = req.notify ? "" : await applyMute(req, created);
  const [invitable] = invitableFromCreated([{ idParticipant, selection: created.selection }]);
  if (invitationBlocked) { return { status: "created", invitable: null, muteError, invitationBlocked }; }
  return { status: "created", invitable: invitable ?? null, muteError };
}

/** Einladung eines Externen (gleicher Endpunkt wie in der Beteiligten-Liste). */
export function inviteExternal (caseId: number, idExternal: number): Promise<unknown> {
  return postRequestGeneric(`/protected/cases/${caseId}/externals/${idExternal}/invite`, {}, true);
}
